import { useState } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { List } from "@mui/material";

import { ConfirmationDialog } from "components/UI/common/ConfirmationDialog/ConfirmationDialog";
import { getNmckBaseData, getNmckList } from "slices/nmck/nmckSelectors";
import { deleteNmckGroup } from "slices/nmck/services/deleteNmckGroup";
import { deleteNmckItem } from "slices/nmck/services/deleteNmckItem";

import { NmckGroupItem } from "./NmckGroupItem";
import styles from "./NmckList.module.css";
import { NmckListItem } from "./NmckListItem";
import { useDragHandlers } from "./useDragHandlers";

export const getTargetNmckId = (list) => {
	let nmckId;
	for (let i = 0; i < list.length; i++) {
		if (list[i].id) {
			nmckId = list[i].id;
			break;
		} else if (list[i]._children) {
			nmckId = getTargetNmckId(list[i]._children);
			if (nmckId) break;
		}
	}
	return nmckId;
};

export const NmckList = ({ showList }) => {
	const { nmckId: currentNmckId, projectId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const nmckList = useSelector(getNmckList);
	const baseData = useSelector(getNmckBaseData);

	const [deleteDialogData, setDeleteDialogData] = useState(null);

	const containerClass = showList ? `${styles.container} ${styles.open}` : `${styles.container}`;
	const isGroup = deleteDialogData?.type === "Group";
	const contentText = isGroup
		? `Все расчеты в группе будут удалены. Удалить группу "${deleteDialogData?.name}"?`
		: `Удалить расчет "${deleteDialogData?.name}"?`;

	const deleteNmck = () => {
		const nmckId = deleteDialogData?.id;
		if (nmckId) {
			dispatch(deleteNmckItem({ nmckId })).then(({ meta, payload }) => {
				if (meta.requestStatus === "fulfilled" && String(nmckId) === currentNmckId) {
					const targetNmckId = getTargetNmckId(payload);
					if (targetNmckId) {
						navigate(`/project/${projectId}/nmck/${targetNmckId}/`);
					} else {
						navigate(`/project/${projectId}/`);
					}
				}
			});
			setDeleteDialogData(null);
		}
	};

	const deleteGroup = () => {
		const groupId = deleteDialogData?.id;
		const currentGroupId = baseData.group_id;
		if (groupId) {
			dispatch(deleteNmckGroup({ groupId })).then(({ meta, payload }) => {
				if (meta.requestStatus === "fulfilled" && groupId === currentGroupId) {
					const targetNmckId = getTargetNmckId(payload.nmck_list);
					if (targetNmckId) {
						navigate(`/project/${projectId}/nmck/${targetNmckId}/`);
					} else {
						navigate(`/project/${projectId}/`);
					}
				}
			});
			setDeleteDialogData(null);
		}
	};

	const { onDragEnd } = useDragHandlers();

	return (
		<div className={containerClass}>
			<div className={styles.wrapper}>
				<div className={styles.title}>
					<span>{baseData?.project_name}</span>
				</div>
				<List sx={{ padding: "0px" }}>
					<DragDropContext onDragEnd={onDragEnd}>
						<Droppable droppableId="nmckList">
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{nmckList.map((item, index) => {
										if (item.type === "Group") {
											return (
												<NmckGroupItem
													key={item.key}
													id={item.group_id}
													index={index}
													name={item.group_name}
													setDeleteDialogData={setDeleteDialogData}
												>
													{item._children}
												</NmckGroupItem>
											);
										}
										return (
											<NmckListItem
												key={item.key}
												id={item.id}
												index={index}
												name={item.name}
												setDeleteDialogData={setDeleteDialogData}
											/>
										);
									})}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</List>
			</div>
			<ConfirmationDialog
				open={Boolean(deleteDialogData)}
				onClose={() => setDeleteDialogData(null)}
				callback={isGroup ? deleteGroup : deleteNmck}
				title="Удаление"
				contentText={contentText}
				okBtnTitle="Удалить"
			/>
		</div>
	);
};
